import { useState } from 'react';
import { ClaimsTab } from '@features/billing/components/v2/claims';
import { RemittancesTab } from '@features/billing/components/v2/remittances';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Card, CardContent, Tab, Tabs, Typography } from '@mui/material';

import BillingMigrationTab from './BillingMigrationTab';

type BillingFilesView = 'import' | 'claims' | 'eras';

interface ViewPanelProps {
  activeView: BillingFilesView;
  children: React.ReactNode;
  value: BillingFilesView;
}

const ViewPanel: React.FC<ViewPanelProps> = ({ activeView, children, value }) => {
  if (activeView !== value) {
    return null;
  }

  return <Box role="tabpanel">{children}</Box>;
};

export default function BillingFilesTab() {
  const [activeView, setActiveView] = useState<BillingFilesView>('import');

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Billing files
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Move billing history into JotPsych or download the original X12 files for another
              billing system.
            </Typography>
          </Box>

          <Tabs
            value={activeView}
            onChange={(_event, value: BillingFilesView) => setActiveView(value)}
            aria-label="Billing file tools"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              px: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 48,
                px: 2,
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .Mui-selected': {
                fontWeight: 600,
              },
            }}
          >
            <Tab
              value="import"
              icon={<UploadFileOutlinedIcon />}
              iconPosition="start"
              label="Import data"
            />
            <Tab
              value="claims"
              icon={<DescriptionOutlinedIcon />}
              iconPosition="start"
              label="Claims · 837"
            />
            <Tab
              value="eras"
              icon={<ReceiptLongOutlinedIcon />}
              iconPosition="start"
              label="ERAs · 835"
            />
          </Tabs>
        </CardContent>
      </Card>

      <ViewPanel activeView={activeView} value="import">
        <BillingMigrationTab />
      </ViewPanel>

      <ViewPanel activeView={activeView} value="claims">
        <BillingFileTable
          title="Claim files"
          description="Select submitted claims to download their original 837 files. You can download one claim from its actions menu or download a selected group as a ZIP."
        >
          <ClaimsTab />
        </BillingFileTable>
      </ViewPanel>

      <ViewPanel activeView={activeView} value="eras">
        <BillingFileTable
          title="ERA files"
          description="Select Stedi remittances to download their original 835 files. Open a remittance to inspect it or download a selected group as a ZIP."
        >
          <RemittancesTab />
        </BillingFileTable>
      </ViewPanel>
    </Box>
  );
}

interface BillingFileTableProps {
  children: React.ReactNode;
  description: string;
  title: string;
}

const BillingFileTable: React.FC<BillingFileTableProps> = ({ children, description, title }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);
